import Icon, { CameraFilled, StarFilled } from "@ant-design/icons";
import { Upload } from "antd";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/auth";
import { IResponse } from "../../../interfaces/IResponse";
import { IUserInformation } from "../../../interfaces/IUser";
import { getUserByID, updateUser } from "../../../services/user";
import globalStyle from "../../styles/globalStyle.module.css";


import EditableText from "../../components/EditableText/EditableText";
import LoadingButton from "../../components/LoadingButton/LoadingButton";
import Paint from "../../components/Paint/Paint";
import { COLORS } from "../../constants/colors";
import allIcons from "../../constants/svgIcon";
import { getError, openNotification, tryCatch } from "../../constants/Utilities";
import './Configuration.css';
import ModalContainer from "../../components/ModalContainer/ModalContainer";

// Interfal Interface
interface ChangeUser {
    email: string;
}

// Props
interface Props {
    setAskBeforeClosing: React.Dispatch<React.SetStateAction<boolean>>;
}

const Profile: React.FC<Props> = ({ setAskBeforeClosing }) => {

    // States
    const { userInformation, setUserInformation } = useAuth();
    const [active, setActive] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [photoPreview, setPhotoPreview] = useState<string>('');

    // Effects
    useEffect(() => {
        setPhotoPreview(!!userInformation?.fotoPerfil ? userInformation!.fotoPerfil : '');
    }, [userInformation]);



    // Methods
    const validations = (values: ChangeUser) => {
        const errors: any = {};

        if (!values.email) {
            errors.email = 'Requerido';
        }

        return errors;
    }

    const getBase64 = (file: any) => {
        if (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif') {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => setPhotoPreview(reader.result as string);
            reader.onerror = error => console.log(error);
        }
        else {
            setPhotoPreview('');
        }
    };

    // Form
    const profileForm = useFormik({
        initialValues: { email: !!userInformation?.correo ? userInformation.correo : '' },
        validate: validations,
        onSubmit: (values: ChangeUser) => handleUpdateUser({ ...userInformation!, correo: values.email, usuarioModificoID: userInformation!.usuarioID, fotoPerfil: photoPreview })
    })

    //Effects
    useEffect(() => {
        setAskBeforeClosing(profileForm.values.email != userInformation?.correo || photoPreview != userInformation.fotoPerfil ? true : false);
    }, [profileForm.values.email, photoPreview, userInformation])



    // Handles
    const handleUpdateUser = (userParam: IUserInformation) => {
        setActive(false);
        setLoading(true);
        tryCatch(() => {
            updateUser(userParam)
                .then((response: IResponse) => {

                    return getUserByID(response.data.usuarioID).then((response2) => {
                        setUserInformation(response2.data);
                        openNotification('success', 'Información modificada', response.msg!);
                    })
                })
                .catch((response) => {
                    openNotification('error', 'Error', getError(response?.msg));
                })
                .finally(() => {
                    setLoading(false);
                })
        })
    }

    return (
        <ModalContainer style={{ height: '76vh' }}>
            <div className="perfil_header">
                <div className="perfil_info">
                    <div className="perfil_name">{userInformation?.primerNombre} {userInformation?.primerApellido}</div>
                    <div className="perfil_code">{userInformation?.nombreUsuario}</div>
                </div>
                {userInformation?.usuarioProyecto!.esAdministrador == true &&
                    <StarFilled className="perfil_admin" />
                }
                <div className="perfil_photoCase">
                    {!!photoPreview
                        ? <img src={photoPreview} alt="Foto de perfil" className="perfil_photo" />
                        : <Icon className="defaultPhoto" component={allIcons.UserIcon} />
                    }
                </div>
                <Upload
                    listType="picture-card"
                    fileList={[]}
                    beforeUpload={getBase64}
                    className="perfil_upload">
                    <CameraFilled className="perfil_iconUpload" />
                </Upload>

                {!!photoPreview &&
                    <div className="image_remove" onClick={() => setPhotoPreview('')}>
                        <i className="fa-solid fa-trash"></i>
                    </div>
                }
            </div>

            <div className="perfil_caseDescription">
                <ul>
                    <li className="perfil_descTitle">Correo</li>
                    <EditableText
                        value={profileForm.values.email}
                        onChange={profileForm.handleChange}
                        backgroundcolor={COLORS.white_color2(1)}
                        name={"email"}
                        placeholder={"Email"}
                        color={COLORS.black_text_color1}
                        active={active}
                        setActive={setActive}
                        editable={true}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{profileForm.errors.email && profileForm.errors.email}</Paint>
                    </p>
                    <li className="perfil_descTitle">Departamento</li>
                    <EditableText
                        value={!!userInformation ? userInformation!.usuarioProyecto!.departamentoNombre! : ""}
                        backgroundcolor={COLORS.white_color2(1)}
                        name={"sales"}
                        placeholder={"Ventas"}
                        color={COLORS.black_text_color1}
                        active={false}
                        editable={false}
                    />
                    <li className="perfil_descTitle">Puesto</li>
                    <EditableText
                        value={!!userInformation ? userInformation!.usuarioProyecto!.puestoNombre! : ""}
                        backgroundcolor={COLORS.white_color2(1)}
                        name={"job"}
                        placeholder={"Puesto"}
                        color={COLORS.black_text_color1}
                        active={false}
                        editable={false}
                    />
                    <li className="perfil_descTitle">Atributos</li>
                    <div className="perfil_listAtri">
                        {!!userInformation && userInformation.usuarioAtributo!.length > 0
                            ?
                            userInformation.usuarioAtributo!.map((atr) => (
                                <div key={atr.atributoNombre + atr.atributoID}>
                                    - {atr.atributoNombre}: <Paint color={COLORS.blue_main(1)}>{atr.atributoValor}</Paint>
                                </div>
                            ))
                            : <p>Sin atributos</p>
                        }
                    </div>
                </ul>
                <LoadingButton
                    type="button"
                    method={profileForm.handleSubmit}
                    title={"Enviar"}
                    loading={loading.toString()}
                    background={COLORS.blue_active}
                    color={COLORS.white_color1}
                    style={{ padding: '3px', width: '200px', position: 'absolute', right: '10px', height: '32px', bottom: 0 }}
                    fontsize={16}
                />
            </div>
        </ModalContainer>

    );
}

export default Profile;